import { useState, useEffect } from 'react'
import { Box, Avatar } from '@mui/material'
import { FormatDateValue } from '../../core/Utilities'
import { Column, ColumnSort, ColumnType, type RowData } from '../Table/DataTable/DataTable.model'
import { type SearchCriteria, usePagination, type PaginationProps } from '../../hooks/use-pagination'
import { sendGet } from '../../hooks/use-fetch'
import DataTable from '../Table/DataTable'
import { TextFieldWithLabel } from '../TextField'
import { useAuth } from '../../hooks/use-auth'
import { Role } from '../../core/types'
import { LargeCard } from '../Cards/Large'
import { ReminderDetails } from './ReminderDetails'
import S3Avatar from '../S3Image/S3Avatar'

export interface ReminderItem {
  reminderID: number
  childID: number
  childName: string
  imageKey: string | null
  caregiverID: number | null
  reminderNote: string
  reminderDate: Date
  pastDue: boolean
}

export function ReminderList (): JSX.Element {
  const [reminderList, setReminderList] = useState<ReminderItem[]>([])
  const [nameFilter, setNameFilter] = useState<string | null>(null)
  const { user } = useAuth()
  const [loaded, setLoaded] = useState<boolean>(false)
  const [selectedReminder, setSelectedReminder] = useState<ReminderItem | null>(null)
  const [refreshTime, setRefreshTime] = useState<Date>(new Date())

  const dateColumn = new Column('reminderDate', 'Date', ColumnType.CUSTOM)
  dateColumn.customCellGenerator = row => {
    return <div className={`d-flex f-align-items-center ${row.pastDue === true ? 'past-due' : ''}`}>
      {FormatDateValue(row.reminderDate)}
    </div>
  }
  const reminderColumn = new Column('reminderNote', 'Reminder')
  const nameColumn = new Column('childName', 'Child Name', ColumnType.CUSTOM)
  nameColumn.customCellGenerator = row => {
    return <div style={{ whiteSpace: 'nowrap' }} >
      {row.imageKey === null
        ? <Avatar sx={{ backgroundColor: '#7136BC', display: 'inline-grid' }}>{String(row.childName)[0]}</Avatar>
        : <S3Avatar imageKey={row.imageKey} height='50px' width='50px' style={{ display: 'inline-grid' }} />}
      <span style={{ whiteSpace: 'nowrap', paddingLeft: '5px' }}>{row.childName}</span>
    </div>
  }

  const columns = [nameColumn, reminderColumn, dateColumn]

  const paginationProps: PaginationProps<RowData> = {
    rows: reminderList,
    initialSize: 5,
    pageSizeSteps: [5, 10]
  }
  const pagination = usePagination(paginationProps)

  useEffect(() => {
    async function fetchData (): Promise<void> {
      const { response } = await sendGet('/Communication/UserReminderList', {})
      if (response !== null) {
        setReminderList(response)
        setLoaded(true)
      }
    }
    void fetchData()
  }, [refreshTime])

  const filterChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value
    setNameFilter(value)
    if (value != null) {
      const fieldArray: SearchCriteria[] = []
      fieldArray.push({
        field: 'childName',
        value: value
      })
      pagination.setSearchFields(fieldArray)
    } else {
      pagination.setSearchFields([])
    }
  }

  const rowClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData): void => {
    setSelectedReminder(row as ReminderItem)
  }

  const closeDialog = (updated: boolean): void => {
    setSelectedReminder(null)
    if (updated) {
      setRefreshTime(new Date())
    }
  }

  const cardContent = (): JSX.Element => {
    if (reminderList.length === 0) {
      return <Box>
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19.9998 8.33301C12.6482 8.33301 6.6665 14.3147 6.6665 21.6663C6.6665 29.018 12.6482 34.9997 19.9998 34.9997C27.3515 34.9997 33.3332 29.018 33.3332 21.6663C33.3332 14.3147 27.3515 8.33301 19.9998 8.33301ZM19.9998 31.6663C14.4848 31.6663 9.99984 27.1813 9.99984 21.6663C9.99984 16.1513 14.4848 11.6663 19.9998 11.6663C25.5148 11.6663 29.9998 16.1513 29.9998 21.6663C29.9998 27.1813 25.5148 31.6663 19.9998 31.6663Z" fill="#722A82" />
          <path d="M18.3333 14.9997H21.6667V23.333H18.3333V14.9997ZM15 3.33301H25V6.66634H15V3.33301ZM32.155 12.8447L28.8217 9.51134L31.1783 7.15467L34.5117 10.488L32.155 12.8447Z" fill="#722A82" />
        </svg>
      </Box>
    } else {
      return <>
        <Box>
          <TextFieldWithLabel
            label='Filter'
            onChange={filterChange}
            name='childName'
            maxWidth='300px'
            value={nameFilter}
            data-testid="name-search"
            placeHolder='Last or First Name'
          />
        </Box>

        <Box sx={{ paddingRight: '30px' }}>
          <DataTable
            name='reminderList'
            columns={columns}
            rows={pagination.internalRows}
            page={pagination.page}
            hasCheckbox={false}
            onPageChange={pagination.handleChangePage}
            onRowClick={rowClick}
            totalRecords={pagination.recordCount}
            onSortChange={(col, sorts) => {
              pagination.onSortChange(col, sorts[0].order)
            }}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
            initialColumnSorts={[new ColumnSort('childName')]}
            loading={false}
          />
        </Box>
      </>
    }
  }

  if (!loaded || user == null || (user.securityLevel !== Role.ISD_USER && user.securityLevel !== Role.INTAKE_USER)) {
    return <></>
  } else {
    return <>
      <LargeCard header='Reminders' headerClassName='info-box-header' sx={{ width: '100%' }} content={cardContent()} />
      {selectedReminder != null && <ReminderDetails data={selectedReminder} onClose={closeDialog} user={user} />}
    </>
  }
}
